"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Gauge, Droplets, Wrench, CircleCheck as CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const benefitsList = [{
  id: "efficiency",
  title: "Eficiência de retenção e consumo",
  category: "Rendimento",
  description: "Gotículas calibradas com tecnologia Spraying Systems Co. que capturam partículas em suspensão com consumo reduzido de água.",
  metricValue: "95%",
  metricLabel: "Taxa média de supressão",
  icon: "Gauge",
  features: ["Dimensão de gota ajustada ao pólen ou poeira fina", "Consumo hídrico até 60% inferior a métodos convencionais", "Cobertura homogénea em correias e pontos de transferência"]
}, {
  id: "compliance",
  title: "Conformidade ambiental e ATEX",
  category: "Normas e Segurança",
  description: "Garantia de conformidade com os limites legais de emissões difusas e prevenção do risco de atmosferas explosivas.",
  metricValue: "100%",
  metricLabel: "Adesão às normas europeias",
  icon: "ShieldCheck",
  features: ["Redução imediata de riscos de explosão por pó combustível", "Cumprimento dos limites de exposição ocupacional (LEO)", "Sistemas certificados para áreas classificadas e ambientes severos"]
}, {
  id: "equipment",
  title: "Proteção mecânica e durabilidade",
  category: "Operação Contínua",
  description: "Menos poeira nos componentes mecânicos traduz-se em menor desgaste de rolamentos, motores e filtros industriais.",
  metricValue: "40%",
  metricLabel: "Menor desgaste de componentes",
  icon: "Wrench",
  features: ["Aumento do intervalo entre manutenções programadas", "Proteção de sensores óticos e sistemas eletrónicos", "Menor necessidade de limpezas manuais em zonas críticas"]
}, {
  id: "sustainability",
  title: "Sustentabilidade e controlo químico",
  category: "Recursos",
  description: "Possibilidade de aplicar agentes tensoativos com dosagem precisa para poeiras hidrófobas e minerais de difícil retenção.",
  metricValue: "3x",
  metricLabel: "Mais eficácia com tensoativos",
  icon: "Droplets",
  features: ["Sistemas automatizados de injeção proporcional", "Menor arrastamento de lamas e resíduos nas instalações", "Impacto positivo nas metas ESG e auditorias ecológicas"]
}];
const technicalMetrics = [{
  value: "+30",
  unit: "Anos",
  label: "Parceria com Spraying Systems Co."
}, {
  value: "1-100",
  unit: "µm",
  label: "Espectro de gota ajustável para cada aplicação"
}, {
  value: ">80%",
  unit: "Menos",
  label: "Emissões fugitivas em pontos de descarga"
}, {
  value: "24/7",
  unit: "Suporte",
  label: "Engenharia e assistência técnica especializada"
}];
export default function TechnicalBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "Gauge":
        return <Gauge className="size-5 text-secondary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-5 text-secondary" />;
      case "Wrench":
        return <Wrench className="size-5 text-secondary" />;
      case "Droplets":
        return <Droplets className="size-5 text-secondary" />;
      default:
        return <Sparkles className="size-5 text-secondary" />;
    }
  };
  return <section id="technicalbenefits" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center mb-16">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Engenharia de Pulverização
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Benefícios técnicos do controlo de poeiras
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            A atomização de precisão da Eurocontrol atua diretamente na raiz do problema, combinando gotas com tamanho microscópico compatível para neutralizar poeiras industriais sem molhar em excesso o material.
          </p>
        </div>

        {/* Benefits Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {benefitsList.map((benefit, i) => <motion.div key={benefit.id} variants={cardVariants} data-index={i}>
              <Card className="h-full border border-border/80 shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-secondary/50 hover:shadow-md bg-card">
                <CardHeader className="flex flex-col gap-3 pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex size-11 items-center justify-center rounded-lg bg-accent">
                      {renderIcon(benefit.icon)}
                    </div>
                    <Badge variant="secondary" className="font-normal text-xs">
                      {benefit.category}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-card-foreground mt-2">
                    {benefit.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex flex-col justify-between pt-2">
                  <div className="space-y-2.5 mb-6">
                    {benefit.features.map((feature, featureIndex) => <div key={featureIndex} data-index={featureIndex} className="flex items-start gap-2.5 text-sm text-card-foreground/90">
                        <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </div>)}
                  </div>

                  <div className="pt-4 border-t border-border flex items-center justify-between">
                    <div>
                      <span className="text-2xl sm:text-3xl font-extrabold text-primary">
                        {benefit.metricValue}
                      </span>
                      <p className="text-xs text-muted-foreground">
                        {benefit.metricLabel}
                      </p>
                    </div>
                    <Button asChild variant="ghost" size="sm" className="group">
                      <Link to="/ApplicationsPage" className="inline-flex items-center gap-1 text-xs font-semibold">
                        Ver aplicações
                        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Technical Metrics Strip */}
        <div className="mt-16 rounded-xl border border-border bg-card p-6 sm:p-8 shadow-xs">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4 md:divide-x md:divide-border">
            {technicalMetrics.map((stat, statIndex) => <div key={statIndex} data-index={statIndex} className={`flex flex-col items-center text-center ${statIndex !== 0 ? "md:pl-6" : ""}`}>
                <div className="flex items-baseline gap-1 text-primary">
                  <span className="text-3xl sm:text-4xl font-bold tracking-tight">
                    {stat.value}
                  </span>
                  <span className="text-sm font-medium text-secondary">
                    {stat.unit}
                  </span>
                </div>
                <p className="mt-2 text-xs sm:text-sm text-muted-foreground">
                  {stat.label}
                </p>
              </div>)}
          </div>
        </div>

        {/* Action Banner */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-xl bg-primary text-primary-foreground p-6 sm:p-8">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-lg sm:text-xl font-bold">
              Precisa de dimensionar um sistema de supressão?
            </h3>
            <p className="text-sm text-primary-foreground/80">
              A nossa equipa técnica avalia o tipo de poeira e calcula o bico e caudal ideais.
            </p>
          </div>
          <div className="flex shrink-0 gap-3">
            <Button asChild variant="secondary" size="lg">
              <Link to="/ContactsPage">Consulte-nos</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/ProductsPage">Ver produtos</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}